import { Step, StepContent, StepLabel, Stepper as MuiStepper } from '@mui/material'
import React, { MutableRefObject, useEffect, useRef } from 'react'
import { FieldValues, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Button from '../actions/Button'

export interface StepConfig<TFormData extends FieldValues = any> {
	key: string
	label: string
	component?: ({ form }: { form: UseFormReturn<TFormData> }) => React.JSX.Element
	element?: React.JSX.Element
	ref?: MutableRefObject<HTMLDivElement | null>
	animationMs?: number
	disabled?: boolean
	disableNext?: () => boolean
	hideNext?: boolean
}

export interface StepperProps<TFormData extends FieldValues = any> {
	steps: StepConfig<TFormData>[]
	activeStep: number
	onNext: () => void
	onPrev: () => void
	form?: UseFormReturn<TFormData>
	lastStepAction?: React.ReactNode
	enableScrolling?: boolean
	transitionDuration?: number
	nonLinear?: boolean
	orientation?: 'horizontal' | 'vertical'
}

/**
 * Reusable Stepper component that wraps Material-UI Stepper with common functionality
 *
 * @example
 * ```tsx
 * const steps: StepConfig[] = [
 *   {
 *     key: 'step1',
 *     label: 'Step 1',
 *     component: MyStepComponent,
 *     disableNext: () => !form.watch('field'),
 *   },
 *   {
 *     key: 'step2',
 *     label: 'Step 2',
 *     element: <CustomElement />,
 *   },
 * ]
 *
 * <Stepper
 *   steps={steps}
 *   activeStep={activeStep}
 *   onNext={handleNext}
 *   onPrev={handlePrev}
 *   form={form}
 * />
 * ```
 */
export const Stepper = <TFormData extends FieldValues = any>({
	steps,
	activeStep,
	onNext,
	onPrev,
	form,
	lastStepAction,
	enableScrolling = true,
	transitionDuration = 200,
	nonLinear = true,
	orientation = 'vertical',
}: StepperProps<TFormData>) => {
	const { t } = useTranslation('common')
	const prevActiveStepRef = useRef(activeStep)

	const enabledSteps = steps.filter(step => !step.disabled)

	const stepsWithRefs = enabledSteps.map(step => ({
		...step,
		ref: step.ref ?? useRef<HTMLDivElement>(null),
	}))

	useEffect(() => {
		prevActiveStepRef.current = activeStep
	}, [activeStep])

	const handleTransitionEnd = (stepRef: MutableRefObject<HTMLDivElement | null>) => {
		if (!enableScrolling) return

		if (prevActiveStepRef.current !== activeStep) {
			setTimeout(() => {
				prevActiveStepRef.current = activeStep
				stepRef?.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				})
			}, 100)
		}
	}

	const renderStepContent = (step: StepConfig<TFormData>) => {
		if (step.component && form) {
			const StepComponent = step.component
			return <StepComponent form={form} />
		}
		if (step.element) {
			return step.element
		}
		return null
	}

	const renderNavigationButtons = (step: StepConfig<TFormData>, stepIndex: number) => {
		const isFirstStep = stepIndex === 0
		const isLastStep = stepIndex === stepsWithRefs.length - 1
		const isNextDisabled = step.disableNext ? step.disableNext() : false

		return (
			<div className='navigate-button-container'>
				{!isFirstStep && (
					<Button
						className='![--background:theme(colors.highlight)] dark:![--background:theme(colors.highlight)] font-semibold tracking-wide'
						onClick={onPrev}
					>
						{t('edellinen')}
					</Button>
				)}
				{!step.hideNext && !isLastStep && (
					<Button
						className='!bg-[linear-gradient(90deg,_#4caf50_0%,_#388e3c_100%)] dark:!bg-[linear-gradient(90deg,_#3ddc84_0%,_#34c98b_100%)]'
						onClick={onNext}
						disabled={isNextDisabled}
					>
						{t('seuraava')}
					</Button>
				)}
				{isLastStep && lastStepAction}
			</div>
		)
	}

	return (
		<MuiStepper nonLinear={nonLinear} activeStep={activeStep} orientation={orientation}>
			{stepsWithRefs.map((step, index) => (
				<Step key={step.key}>
					<div ref={step.ref}></div>
					<StepLabel>{step.label}</StepLabel>
					<StepContent
						onTransitionEnd={() => handleTransitionEnd(step.ref!)}
						transitionDuration={transitionDuration}
					>
						{renderStepContent(step)}
						{renderNavigationButtons(step, index)}
					</StepContent>
				</Step>
			))}
		</MuiStepper>
	)
}

export default Stepper
